/**
 * Redux slice for Drawing and Annotation Tools state management.
 *
 * Manages active drawing tool, drawings list, style settings,
 * and text annotation state.
 *
 * Validates: Requirements 39.1, 39.2, 39.4, 39.5, 39.6, 40.1, 40.2, 40.4, 40.5, 40.6
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Drawing, DrawingStyle, DrawingToolType, TextStyle } from '../../types/drawing';
import { DEFAULT_DRAWING_STYLE, DEFAULT_TEXT_STYLE } from '../../types/drawing';

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------

export interface DrawingState {
  /** Currently active drawing tool (null = no tool active) */
  activeTool: DrawingToolType;
  /** List of all drawings on the map */
  drawings: Drawing[];
  /** Current drawing style for shapes */
  drawingStyle: DrawingStyle;
  /** Current text annotation style */
  textStyle: TextStyle;
  /** Whether the text input dialog is open */
  textDialogOpen: boolean;
  /** Coordinates where text annotation will be placed */
  textPlacementCoords: [number, number] | null;
  /** ID of drawing currently being edited (for text edit) */
  editingDrawingId: string | null;
  /** Whether drawings have been saved as a layer */
  savedAsLayer: boolean;
}

const initialState: DrawingState = {
  activeTool: null,
  drawings: [],
  drawingStyle: DEFAULT_DRAWING_STYLE,
  textStyle: DEFAULT_TEXT_STYLE,
  textDialogOpen: false,
  textPlacementCoords: null,
  editingDrawingId: null,
  savedAsLayer: false,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const drawingSlice = createSlice({
  name: 'drawing',
  initialState,
  reducers: {
    /** Set the active drawing tool */
    setActiveTool: (state, action: PayloadAction<DrawingToolType>) => {
      state.activeTool = action.payload;
    },

    /** Add a new drawing to the list */
    addDrawing: (state, action: PayloadAction<Drawing>) => {
      state.drawings.push(action.payload);
      state.savedAsLayer = false;
    },

    /** Update an existing drawing by ID */
    updateDrawing: (state, action: PayloadAction<{ id: string; updates: Partial<Drawing> }>) => {
      const { id, updates } = action.payload;
      const index = state.drawings.findIndex((d) => d.id === id);
      if (index !== -1) {
        state.drawings[index] = { ...state.drawings[index], ...updates };
        state.savedAsLayer = false;
      }
    },

    /** Delete a drawing by ID */
    deleteDrawing: (state, action: PayloadAction<string>) => {
      state.drawings = state.drawings.filter((d) => d.id !== action.payload);
      state.savedAsLayer = false;
    },

    /** Clear all drawings */
    clearAllDrawings: (state) => {
      state.drawings = [];
      state.savedAsLayer = false;
    },

    /** Update the drawing style for shapes */
    setDrawingStyle: (state, action: PayloadAction<Partial<DrawingStyle>>) => {
      state.drawingStyle = { ...state.drawingStyle, ...action.payload };
    },

    /** Update the text annotation style */
    setTextStyle: (state, action: PayloadAction<Partial<TextStyle>>) => {
      state.textStyle = { ...state.textStyle, ...action.payload };
    },

    /** Open text input dialog at specified coordinates */
    openTextDialog: (state, action: PayloadAction<[number, number]>) => {
      state.textDialogOpen = true;
      state.textPlacementCoords = action.payload;
      state.editingDrawingId = null;
    },

    /** Open text input dialog for editing existing annotation */
    openTextEditDialog: (state, action: PayloadAction<string>) => {
      state.textDialogOpen = true;
      state.editingDrawingId = action.payload;
      state.textPlacementCoords = null;
    },

    /** Close text input dialog */
    closeTextDialog: (state) => {
      state.textDialogOpen = false;
      state.textPlacementCoords = null;
      state.editingDrawingId = null;
    },

    /** Mark drawings as saved to a layer */
    markSavedAsLayer: (state) => {
      state.savedAsLayer = true;
    },

    /** Reset drawing state */
    resetDrawing: () => initialState,
  },
});

export const {
  setActiveTool,
  addDrawing,
  updateDrawing,
  deleteDrawing,
  clearAllDrawings,
  setDrawingStyle,
  setTextStyle,
  openTextDialog,
  openTextEditDialog,
  closeTextDialog,
  markSavedAsLayer,
  resetDrawing,
} = drawingSlice.actions;

export default drawingSlice.reducer;
